// The auth facade: the AuthService contract below and its one implementation.
// Consumers — the router guard, common/authorization.ts bearerHeader,
// currentUser, NavBar — import this default export and never see the
// provider. The only provider is Heavybit Identity (stytchSession); the
// Stytch SDK itself is a dynamic import inside src/identity/stytch.ts.
import stytchSession from "@/identity/stytchSession"
import type { IdentityPrincipal } from "@/identity/waitlist"

// The decoded token payload the user store reads x-hasura-user-id from
// (stores/user.ts updateAuthInfo): the Stytch session JWT payload. Only the
// Hasura block is contractually present.
export interface AuthProfile {
  [key: string]: unknown
  "https://hasura.io/jwt/claims"?: {
    "x-hasura-user-id"?: string
    "x-hasura-person-id"?: string
    "x-hasura-allowed-roles"?: unknown
    [key: string]: unknown
  }
}

export interface AuthSession {
  loggedIn: boolean
  profile: AuthProfile | null
}

export interface AuthService {
  /** False in the dev/test bypass (VITE_AUTH_DISABLED); every method is then inert. */
  enabled: boolean
  /** Resolves once the provider SDK has hydrated its cached state. Guard and token calls await it. */
  ready(): Promise<void>
  isAuthenticated(): boolean
  /** The bearer token for Hasura and the Netlify functions: the Stytch session JWT. Refreshes transparently; null when signed out. */
  getIdToken(): Promise<string | null>
  /** Bypass the SDK's local expiry check and mint a fresh token (the invalid-jwt recovery path in init/apollo.ts). Single-flight. */
  forceRefreshToken(): Promise<string | null>
  /** Start the login flow, preserving `target` (an in-app path) for after the redirect. Never resolves back into the caller's flow. */
  login(target?: string): void
  /** Resolve the session once per page load: hydrate, finish any redirect landing, set CloudFront cookies, report state for the user store. */
  resolveSession(): Promise<AuthSession>
  logout(): void
  /**
   * The routing fact carried by the current token: its `roles`
   * (identity/waitlist.ts). Read synchronously from what the provider already
   * holds — the router guard calls it before any store loads, so it must not
   * make a request. A token without the claim answers `{ roles: [] }`.
   */
  principal(): IdentityPrincipal
  /**
   * The Hasura roles the current token allows (`x-hasura-allowed-roles`).
   * Read synchronously from the token the provider holds, like principal().
   * No session, or a token without the claim, answers `[]`.
   */
  allowedRoles(): string[]
  /**
   * Finish a redirect landing. /identity/callback
   * adopts the exchanged session into the SDK; on the identity routes this
   * resolves once the SDK holds that session (no second exchange), and is a
   * no-op everywhere else. resolveSession() awaits it.
   */
  handleRedirectCallback(): Promise<void>
}

const authService: AuthService = stytchSession

export default authService
